import { useState } from 'react';
import { X, Plus, Trash2, ChevronUp, ChevronDown, Database } from 'lucide-react';

const INDEX_TYPES = [
  { value: 'Clustered', label: 'Clustered', description: 'Physical order of data' },
  { value: 'Non-Clustered', label: 'Non-Clustered', description: 'Logical index structure' },
  { value: 'Unique', label: 'Unique', description: 'Enforces unique values' },
  { value: 'Composite', label: 'Composite', description: 'Multiple columns' },
  { value: 'Hash', label: 'Hash', description: 'Hash-based lookup' },
  { value: 'Geospatial', label: 'Geospatial', description: 'Geographic data' },
  { value: 'Full-Text', label: 'Full-Text', description: 'Text search' }
];

const ManageIndexesModal = ({ isOpen, onClose, entityName, fields, virtualFKFields = [], indexes, onSave }) => {
  const [localIndexes, setLocalIndexes] = useState(indexes || []);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newIndex, setNewIndex] = useState({
    name: '',
    type: 'Non-Clustered',
    columns: []
  });

  if (!isOpen) return null;

  // Combine real fields with virtual FK fields for selection
  const allSelectableFields = [
    ...fields,
    ...virtualFKFields.map(fk => ({
      name: fk.sourceName,
      type: 'FK',
      isPrimaryKey: false,
      isForeignKey: true,
      isVirtual: true
    }))
  ];

  const generateId = () => `idx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const handleAddColumn = () => {
    if (allSelectableFields.length === 0) return;
    
    setNewIndex({
      ...newIndex,
      columns: [...newIndex.columns, { fieldName: allSelectableFields[0].name, order: 'ASC' }]
    });
  };

  const handleRemoveColumn = (columnIndex) => {
    setNewIndex({
      ...newIndex,
      columns: newIndex.columns.filter((_, i) => i !== columnIndex)
    });
  };

  const handleColumnChange = (columnIndex, field, value) => {
    const updatedColumns = [...newIndex.columns];
    updatedColumns[columnIndex] = {
      ...updatedColumns[columnIndex],
      [field]: value
    };
    setNewIndex({
      ...newIndex,
      columns: updatedColumns
    });
  };

  const handleMoveColumn = (columnIndex, direction) => {
    const updatedColumns = [...newIndex.columns];
    const newIndex_pos = columnIndex + direction;
    
    if (newIndex_pos < 0 || newIndex_pos >= updatedColumns.length) return;
    
    [updatedColumns[columnIndex], updatedColumns[newIndex_pos]] = 
    [updatedColumns[newIndex_pos], updatedColumns[columnIndex]];
    
    setNewIndex({
      ...newIndex,
      columns: updatedColumns
    });
  };

  const handleCreateIndex = () => {
    if (!newIndex.name.trim()) {
      alert('Please enter an index name');
      return;
    }

    if (newIndex.columns.length === 0) {
      alert('Please add at least one column to the index');
      return;
    }

    const indexToAdd = {
      id: generateId(),
      ...newIndex
    };

    setLocalIndexes([...localIndexes, indexToAdd]);
    setNewIndex({
      name: '',
      type: 'Non-Clustered',
      columns: []
    });
  };

  const handleDeleteIndex = (indexId) => {
    setLocalIndexes(localIndexes.filter(idx => idx.id !== indexId));
  };

  const handleEditIndex = (index) => {
    setEditingIndex(index.id);
    setNewIndex({
      name: index.name,
      type: index.type,
      columns: [...index.columns]
    });
    handleDeleteIndex(index.id);
  };

  const handleSave = () => {
    onSave(localIndexes);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Database size={24} />
              Manage Indexes
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Entity: <span className="font-semibold">{entityName}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-700 dark:text-gray-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Existing Indexes */}
          {localIndexes.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                Existing Indexes ({localIndexes.length})
              </h3>
              <div className="space-y-3">
                {localIndexes.map((index) => (
                  <div
                    key={index.id}
                    className="border-2 border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900/50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {index.name}
                        </h4>
                        <span className="inline-block px-2 py-1 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 mt-1">
                          {index.type}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditIndex(index)}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteIndex(index.id)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Columns:</span>{' '}
                      {index.columns.map((col, i) => (
                        <span key={i}>
                          {col.fieldName} ({col.order})
                          {i < index.columns.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Create New Index */}
          <div className="border-2 border-blue-200 dark:border-blue-800 rounded-lg p-6 bg-blue-50 dark:bg-blue-900/10">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {editingIndex ? 'Edit Index' : 'Create New Index'}
            </h3>

            {/* Index Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Index Name *
              </label>
              <input
                type="text"
                value={newIndex.name}
                onChange={(e) => setNewIndex({ ...newIndex, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="e.g., idx_user_email, idx_created_at"
              />
            </div>

            {/* Index Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Index Type *
              </label>
              <select
                value={newIndex.type}
                onChange={(e) => setNewIndex({ ...newIndex, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {INDEX_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label} - {type.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Columns */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Columns *
                </label>
                <button
                  onClick={handleAddColumn}
                  disabled={allSelectableFields.length === 0}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  <Plus size={14} />
                  Add Column
                </button>
              </div>

              {newIndex.columns.length === 0 ? (
                <div className="text-sm text-gray-500 dark:text-gray-400 italic p-4 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center">
                  No columns added yet. Click "Add Column" to start.
                </div>
              ) : (
                <div className="space-y-2">
                  {newIndex.columns.map((column, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700"
                    >
                      <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-6">
                        {index + 1}.
                      </span>
                      <select
                        value={column.fieldName}
                        onChange={(e) => handleColumnChange(index, 'fieldName', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        {allSelectableFields.map((field) => (
                          <option key={field.name} value={field.name}>
                            {field.name} ({field.type})
                            {field.isPrimaryKey ? ' - PK' : ''}
                            {field.isForeignKey ? ' - FK' : ''}
                            {field.isVirtual ? ' - Virtual FK' : ''}
                          </option>
                        ))}
                      </select>
                      <select
                        value={column.order}
                        onChange={(e) => handleColumnChange(index, 'order', e.target.value)}
                        className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      >
                        <option value="ASC">ASC</option>
                        <option value="DESC">DESC</option>
                      </select>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleMoveColumn(index, -1)}
                          disabled={index === 0}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUp size={16} />
                        </button>
                        <button
                          onClick={() => handleMoveColumn(index, 1)}
                          disabled={index === newIndex.columns.length - 1}
                          className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDown size={16} />
                        </button>
                        <button
                          onClick={() => handleRemoveColumn(index)}
                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Create Button */}
            <button
              onClick={handleCreateIndex}
              disabled={!newIndex.name.trim() || newIndex.columns.length === 0}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Plus size={16} />
              {editingIndex ? 'Update Index' : 'Create Index'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            Save All Indexes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageIndexesModal;
